import { makeAutoObservable } from 'mobx';

import { RootStore } from './';

type SearchParams = {
  type?: 'all' | 'manage-all' | 'manage-completed';
  sortDirection?: string;
  sortBy?: string;
  sortChanged?: boolean;
  primaryDriverMatchStatus?:
    | ''
    | 'All'
    | 'No match'
    | 'Auto-match'
    | 'Confirmed';
  secondaryDriverMatchStatus?:
    | ''
    | 'All'
    | 'No match'
    | 'Auto-match'
    | 'Confirmed';
  truckMatchStatus?: '' | 'All' | 'No match' | 'Auto-match' | 'Confirmed';
  trailerMatchStatus?: '' | 'All' | 'No match' | 'Auto-match' | 'Confirmed';
  primaryDriverName?: string;
  matchedPrimaryDriverName?: string;
  secondaryDriverName?: string;
  matchedSecondaryDriverName?: string;
  powerUnitVin?: string;
  matchedPowerUnitVin?: string;
  trailerVin?: string;
  matchedTrailerVin?: string;
  reportNumber?: string;
  inspectionState?: string;
  inspectionDateStart?: string;
  inspectionDateEnd?: string;
  createdDate?: string;
  inspectionId?: string;
};

type getEquipmentListParams = {
  type?: 'power unit' | 'trailer';
  vin?: string;
  nextLink?: string | number;
  limit?: number;
  unitId?: string;
};

type getDriverListParams = {
  name?: string;
  nextLink?: string | number;
  limit?: number;
  driverId?: string;
};

const removeEmptyValues = (obj: Record<string, any>) => {
  const cleaned: Record<string, any> = {};
  for (const key in obj) {
    if (!Object.prototype.hasOwnProperty.call(obj, key)) {
      continue;
    }
    const value = obj[key];
    if (value !== undefined && value !== null && value !== '') {
      cleaned[key] = value;
    }
  }
  return cleaned;
};

// type submitDataQParams = {
//   challengeReason: string;
//   challengeType: string;
//   reasonType: string;
//   supportDocuments?: File[];
//   explanation?: string;
//   violationCodes?: string[];
//   source: string;
// };

export class InspectionsAndViolationsStore {
  private _list: Inspection[];
  private _activeListRequestId = 0;
  private _limit = 50;
  private _showLoader = false;
  private _hasMoreResults = true;
  private _nextLink = '';
  private _count = 0;
  // private _equipmentSelectedList: Array<any> = [];
  public _status = 'All';
  private _sortDirection = '';
  private _sortBy = '';
  private _sortChanged = false;
  // private _reportNumber = '';
  // private _inspectionState = '';
  private _searchFilterParams: SearchParams = {
    inspectionDateEnd: '',
    inspectionDateStart: '',
    inspectionId: '',
    inspectionState: '',
    matchedPowerUnitVin: '',
    matchedPrimaryDriverName: '',
    matchedSecondaryDriverName: '',
    matchedTrailerVin: '',
    powerUnitVin: '',
    primaryDriverMatchStatus: 'All',
    primaryDriverName: '',
    reportNumber: '',
    secondaryDriverMatchStatus: 'All',
    secondaryDriverName: '',
    trailerMatchStatus: 'All',
    trailerVin: '',
    truckMatchStatus: 'All',
  };

  private _manageType: 'all' | 'manage-all' | 'manage-completed';
  private _resetManageSelectAll = false;

  constructor(public rootStore: RootStore) {
    this._list = [];
    this._count = 0;
    this._manageType = 'manage-all';
    makeAutoObservable(this, {
      rootStore: false,
    });
  }

  setShowLoader(value: boolean) {
    this._showLoader = value;
  }

  setNextLink(link: string) {
    this._nextLink = link;
  }

  setCount(count: number) {
    this._count = count;
  }

  setHasMoreResults(hasMoreResults: boolean) {
    this._hasMoreResults = hasMoreResults;
  }

  setSortBy(data: string) {
    this._sortBy = data;
  }

  setStatus(value: string) {
    this._status = value;
  }

  setSortDirection(data: string) {
    this._sortDirection = data;
  }

  setSortChanged(sortChanged: boolean) {
    this._sortChanged = sortChanged;
  }

  resetSortParams() {
    this._sortBy = '';
    this._sortDirection = '';
    this._sortChanged = true;
  }

  setSearchParams(filters: any) {
    this._searchFilterParams = {
      ...this._searchFilterParams,
      ...filters,
    };
  }

  resetSearchParams() {
    this.setSearchParams({
      createdDateEnd: '',
      createdDateStart: '',
      inspectionDateEnd: '',
      inspectionDateStart: '',
      inspectionId: '',
      inspectionState: '',
      matchedPowerUnitVin: '',
      matchedPrimaryDriverName: '',
      matchedSecondaryDriverName: '',
      matchedTrailerVin: '',
      powerUnitVin: '',
      primaryDriverMatchStatus: 'All',
      primaryDriverName: '',
      reportNumber: '',
      secondaryDriverMatchStatus: 'All',
      secondaryDriverName: '',
      trailerMatchStatus: 'All',
      trailerVin: '',
      truckMatchStatus: 'All',
    });
  }

  setManageType(data: 'manage-all' | 'manage-completed') {
    this._manageType = data;
  }

  setResetManageSelectAll(value: boolean) {
    this._resetManageSelectAll = value;
  }
  get ResetManageSelectAll() {
    return this._resetManageSelectAll;
  }

  get SearchParams() {
    return this._searchFilterParams;
  }

  get Data() {
    return this._list;
  }

  get NextLink() {
    return this._nextLink;
  }

  get Count() {
    return this._count;
  }

  get HasMoreResults() {
    return this._hasMoreResults;
  }

  get SortBy() {
    return this._sortBy;
  }

  get Status() {
    return this._status;
  }

  get SortDirection() {
    return this._sortDirection;
  }

  get sortChanged() {
    return this._sortChanged;
  }

  get ShowLoader() {
    return this._showLoader;
  }

  get ManageType() {
    return this._manageType;
  }

  resetParams() {
    this._activeListRequestId += 1;
    this._nextLink = '';
    this._list = [];
  }

  private buildListParams(
    type: 'all' | 'manage-all' | 'manage-completed',
    includeInspectionId: boolean,
  ): any {
    return removeEmptyValues({
      createdDate: this._searchFilterParams.createdDate,
      inspectionDateEnd: this._searchFilterParams.inspectionDateEnd,
      inspectionDateStart: this._searchFilterParams.inspectionDateStart,
      inspectionId: includeInspectionId
        ? this._searchFilterParams.inspectionId
        : undefined,
      inspectionState: this._searchFilterParams.inspectionState,
      limit: this._limit,
      matchedPowerUnitVin: this._searchFilterParams.matchedPowerUnitVin,
      matchedPrimaryDriverName:
        this._searchFilterParams.matchedPrimaryDriverName,
      matchedSecondaryDriverName:
        this._searchFilterParams.matchedSecondaryDriverName,
      matchedTrailerVin: this._searchFilterParams.matchedTrailerVin,
      nextLink: this._nextLink,
      powerUnitVin: this._searchFilterParams.powerUnitVin,
      primaryDriverMatchStatus:
        this._searchFilterParams.primaryDriverMatchStatus === 'All'
          ? ''
          : this._searchFilterParams.primaryDriverMatchStatus,
      primaryDriverName: this._searchFilterParams.primaryDriverName,
      reportNumber: this._searchFilterParams.reportNumber,
      secondaryDriverMatchStatus:
        this._searchFilterParams.secondaryDriverMatchStatus === 'All'
          ? ''
          : this._searchFilterParams.secondaryDriverMatchStatus,
      secondaryDriverName: this._searchFilterParams.secondaryDriverName,
      sortBy: this._sortBy,
      sortChanged: this._sortChanged,
      sortDirection: this._sortDirection,
      trailerMatchStatus:
        this._searchFilterParams.trailerMatchStatus === 'All'
          ? ''
          : this._searchFilterParams.trailerMatchStatus,
      trailerVin: this._searchFilterParams.trailerVin,
      truckMatchStatus:
        this._searchFilterParams.truckMatchStatus === 'All'
          ? ''
          : this._searchFilterParams.truckMatchStatus,
      type,
    });
  }

  getList = async () => {
    const requestId = ++this._activeListRequestId;
    const _type = this.Status === 'All' ? 'all' : this._manageType;
    const params = this.buildListParams(_type, true);
    const { list, nextLink, count } =
      await this.rootStore.apiStore.inspectionsAndViolationsApi.getData(params);

    // Ignore stale responses from previous requests (e.g. old filters/page state).
    if (requestId !== this._activeListRequestId) {
      return;
    }

    this._list = [...this._list, ...list];
    if (nextLink === this._nextLink || list.length < this._limit) {
      this._nextLink = '';
    } else {
      this._nextLink = nextLink;
    }
    this._count = count;
  };

  getMoreDetailsByInspectionId = (id: string) =>
    this.rootStore.apiStore.inspectionsAndViolationsApi.getMoreDetails(id);

  exportList = async () => {
    const _type = this.Status === 'All' ? 'all' : this._manageType;
    const params = this.buildListParams(_type, false);
    return await this.rootStore.apiStore.inspectionsAndViolationsApi.export(
      params,
    );
  };

  getInspectionDriverViolationMatch = async (inspectionIds: string[]) => {
    return await this.rootStore.apiStore.inspectionsAndViolationsApi.getInspectionDriverViolationMatch(
      inspectionIds,
    );
  };

  confirmMatch = async (payload: any) => {
    const resp =
      await this.rootStore.apiStore.inspectionsAndViolationsApi.matchDriversForInspectionsAndViolations(
        payload,
      );
    return resp;
  };

  getAllDrivers = async (params: getDriverListParams = {}) => {
    const resp =
      await this.rootStore.apiStore.inspectionsAndViolationsApi.getAllDrivers(
        params,
      );
    return resp;
  };

  getAllEquipments = async (params: getEquipmentListParams = {}) => {
    const resp =
      await this.rootStore.apiStore.inspectionsAndViolationsApi.getAllEquipments(
        params,
      );
    return resp;
  };

  submitDataQChallenge = async (payload: any) => {
    const resp =
      await this.rootStore.apiStore.inspectionsAndViolationsApi.submitDataQChallenge(
        payload,
      );
    return resp;
  };
}
